import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import Table, { type Header, type Row } from 'src/components/Table';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { can, Can, Subject } from 'src/hooks/usePermissions';
import type { WorkflowContactWindow } from 'src/interfaces/workflows.interface';
import { formatTimeLabel } from 'src/utils/date';
import {
  formatWorkflowTimezone,
  getEffectiveTimeZone,
} from 'src/utils/contactWindowUtils';
import { days } from './constants';
import { SideDropActions } from 'src/components/SideDropActions';
import { Pencil } from '@phosphor-icons/react';
import { useEnvironment } from 'src/contexts/EnvironmentContext';

export const ListContactWindows = () => {
  const navigate = useNavigate();
  const { get } = useAuthedRequest();
  const { role } = useEnvironment();
  const [contactWindows, setContactWindows] = useState<WorkflowContactWindow[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchContactWindows = async () => {
      setLoading(true);
      try {
        const fetchedContactWindows = (await get(
          '/workflow_contact_windows',
        )) as WorkflowContactWindow[];
        const records = Array.isArray(fetchedContactWindows)
          ? fetchedContactWindows
          : [];
        setContactWindows(records);
      } catch (error) {
        console.error('Failed to fetch contact windows:', error);
        setContactWindows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContactWindows();
  }, [get]);

  const formatDays = (daysArray: number[]): string => {
    if (daysArray.length === 0) {
      return '-';
    }
    return daysArray
      .slice()
      .sort((a, b) => a - b)
      .map((dayValue) => {
        const dayOption = days.find((d) => d.value === dayValue);
        return dayOption ? dayOption.label : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  const headers: Header[] = [
    { key: 'label', label: 'Name', width: '20%', disableSorting: true },
    {
      key: 'description',
      label: 'Description',
      width: '25%',
      disableSorting: true,
    },
    { key: 'days', label: 'Days', width: '15%', disableSorting: true },
    {
      key: 'timeRange',
      label: 'Time Range',
      width: '15%',
      disableSorting: true,
    },
    { key: 'timezone', label: 'Timezone', width: '15%', disableSorting: true },
    { key: 'actions', label: '', width: '10%', disableSorting: true },
  ];

  const rows: Row[] = contactWindows.map((window: WorkflowContactWindow) => ({
    label: window.label || '-',
    description: window.description || '-',
    days: formatDays(window.days),
    timeRange:
      window.startTime && window.endTime
        ? `${formatTimeLabel(window.startTime)} - ${formatTimeLabel(
            window.endTime,
          )}`
        : '-',
    timezone: (
      <span className="text-sm">
        {getEffectiveTimeZone(window)
          ? formatWorkflowTimezone(getEffectiveTimeZone(window)!, {
              condensed: true,
            })
          : '-'}
      </span>
    ),
    actions: (
      <div
        className="flex justify-center items-center"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <SideDropActions
          options={[
            {
              label: 'Edit',
              Icon: Pencil,
              show: can(role, 'update', Subject.CAMPAIGNS),
              onClick: () =>
                navigate(
                  `/beta/campaigns/contact-window?id=${window.id}&returnTo=/beta/campaigns/contact-windows`,
                ),
            },
          ]}
        />
      </div>
    ),
  }));

  return (
    <>
      <Heading
        title="Campaign Schedules"
        subtitle="Manage outreach schedules for your campaigns."
      />

      <ContentWrapper>
        <div className="flex flex-col md:flex-row justify-end mb-6">
          <Can operation="create" subject={Subject.CAMPAIGNS}>
            <Button
              onClick={() =>
                navigate(
                  '/beta/campaigns/contact-window?returnTo=/beta/campaigns/contact-windows',
                )
              }
              className="h-11 whitespace-nowrap"
            >
              New Campaign Schedule
            </Button>
          </Can>
        </div>

        <Can operation="read" subject={Subject.CAMPAIGNS}>
          <Table
            headers={headers}
            rows={rows}
            totalItems={contactWindows.length}
            currentPage={1}
            onPageChange={() => {}}
            onSort={() => {}}
            onRowClick={(rowIndex) => {
              const contactWindow = contactWindows[rowIndex];
              if (!contactWindow?.id) return;

              navigate(
                `/beta/campaigns/contact-window?id=${contactWindow.id}&returnTo=/beta/campaigns/contact-windows`,
              );
            }}
            loading={loading}
            defaultSize={pageSize}
            setSize={setPageSize}
            sortConfig={{ key: null, direction: null }}
          />
        </Can>
      </ContentWrapper>
    </>
  );
};
